import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import appAPI from '../../api/api'
import {
  CriterionAspectForModuleCriterion,
  CriterionAspectForModuleCriterionRequest,
  EvalModule,
  EvalModuleRequest,
  EvalStructure,
  EvalStructureRequest,
  ModuleCriterion,
  TeamDetail,
} from '../../api/model/models'
import { RootState } from '../store'
import { ratingParticipantsSliceActions } from './ratingParticipantsSlice'

interface GetRatingModulesArgs {
  competenciesSelectID: string | number
  championshipsSelectID: string | number
  file: File
}

interface TInitialState {
  ratingEntryStatus: 'customization' | 'estimation' | 'blocked' | undefined
  competenciesSelectID: number | undefined
  championshipsSelectID: number | undefined
  addNewModule: EvalModule | undefined
  uploadFileLoading: boolean
  uploadFileError: boolean
  emptyRatingData: boolean
  ratingModules: {
    loading: boolean
    data: EvalStructure | null | undefined
  }
  ratingModuleDetail: {
    loading: boolean
    data: EvalModule | null | undefined
    updateStatus: 'success' | 'error' | undefined
    criteriaDeleteStatus: 'success' | 'error' | undefined
  }
  ratingCriterionDetails: {
    loading: boolean
    data: ModuleCriterion | null | undefined
  }
  evaluationDetail: {
    loading: boolean
    data: TeamDetail | null | undefined
    latestRequestId?: string
  }
}

const initialState: TInitialState = {
  ratingEntryStatus: undefined,
  competenciesSelectID: undefined,
  championshipsSelectID: undefined,
  addNewModule: undefined,
  uploadFileLoading: false,
  uploadFileError: false,
  emptyRatingData: false,
  ratingModules: {
    loading: false,
    data: undefined,
  },
  ratingModuleDetail: {
    loading: false,
    data: undefined,
    updateStatus: undefined,
    criteriaDeleteStatus: undefined,
  },
  ratingCriterionDetails: {
    loading: false,
    data: undefined,
  },
  evaluationDetail: {
    loading: false,
    data: undefined,
    latestRequestId: undefined,
  },
}

const uploadModulesFile = createAsyncThunk('uploadModulesFile', async (props: GetRatingModulesArgs, { dispatch }) => {
  const { championshipsSelectID, competenciesSelectID, file } = props

  const uploadResult = await appAPI.skillsPassport.uploadModulesFile(championshipsSelectID, competenciesSelectID, file)

  dispatch(ratingSliceActions.getEvalStructure({ championshipsSelectID, competenciesSelectID }))

  return uploadResult
})

const getEvalStructure = createAsyncThunk(
  'getEvalStructure',
  async (props: Omit<GetRatingModulesArgs, 'file'>, { dispatch, getState, rejectWithValue }) => {
    let createEvalStructure = false

    const state = getState() as RootState
    const championship = state.championships

    if (championship.championshipId === +props.championshipsSelectID) {
      const competence = championship.competences.find((item) => item.id === props.competenciesSelectID)
      if (competence) {
        createEvalStructure = true
      }
    }

    try {
      return await appAPI.skillsPassport.getEvalStructure(props.championshipsSelectID, props.competenciesSelectID)
    } catch (error: any) {
      const evalStructureError = rejectWithValue({
        messages: error.response.data.detail,
        status: error.response.status,
      }) as any

      if (
        createEvalStructure &&
        evalStructureError?.payload.messages === 'Не найдено.' &&
        evalStructureError.payload?.status === 404
      ) {
        dispatch(
          ratingSliceActions.createEvalStructure({
            championship: +props.championshipsSelectID,
            competence: +props.competenciesSelectID,
          }),
        )
      }

      return evalStructureError
    }
  },
)

const createEvalStructure = createAsyncThunk(
  'createEvalStructure',
  async (props: { championship: number; competence: number }, { dispatch }) => {
    const res = await appAPI.skillsPassport.createEvalStructure(props)
    if (res.status === 201) {
      dispatch(
        ratingSliceActions.getEvalStructure({
          championshipsSelectID: props.championship,
          competenciesSelectID: props.competence,
        }),
      )
    }

    return res
  },
)

const updateEvalStructure = createAsyncThunk(
  'updateEvalStructure',
  async (props: EvalStructureRequest & { evalStructureId: number | string }, { dispatch }) => {
    const { evalStructureId, ...body } = props

    const res = await appAPI.skillsPassport.updateEvalStructure(evalStructureId, body)

    dispatch(
      ratingSliceActions.getEvalStructure({
        championshipsSelectID: props.championship,
        competenciesSelectID: props.competence,
      }),
    )

    return res
  },
)

const getRatingModuleDetails = createAsyncThunk('getRatingModuleDetails', async (moduleId: string) => {
  return await appAPI.skillsPassport.getModuleDetails(moduleId)
})

const createRatingModule = createAsyncThunk('createRatingModule', async (props: EvalModuleRequest) => {
  return await appAPI.skillsPassport.createModule(props)
})

const updateRatingModuleDetails = createAsyncThunk(
  'updateRatingModuleDetails',
  async (props: { moduleId: number; evalStructure: number; title: string }, { dispatch, getState }) => {
    const updateModule = await appAPI.skillsPassport.updateModuleDetails(props)

    const state = getState() as RootState
    const moduleId = state.rating.ratingModuleDetail.data?.id

    dispatch(ratingSliceActions.getRatingModuleDetails(String(moduleId)))

    return updateModule
  },
)

const deleteRatingModuleDetails = createAsyncThunk(
  'deleteRatingModuleDetails',
  async (moduleId: number, { dispatch, getState }) => {
    await appAPI.skillsPassport.deleteModuleDetails(moduleId)

    const state = getState() as RootState

    const championshipsSelectID = state.rating.championshipsSelectID
    const competenciesSelectID = state.rating.competenciesSelectID

    if (championshipsSelectID && competenciesSelectID)
      dispatch(ratingSliceActions.getEvalStructure({ championshipsSelectID, competenciesSelectID }))
  },
)

const getRatingCriterionDetails = createAsyncThunk('getCriterionDetails', (criterionId: number) => {
  return appAPI.skillsPassport.getCriterionDetails(criterionId)
})

const createRatingCriteria = createAsyncThunk(
  'createRatingCriteria',
  async (props: { module: number; title: string }, { dispatch }) => {
    const addCriteria = await appAPI.skillsPassport.createCriteria(props)

    const moduleId = addCriteria.data.module

    dispatch(ratingSliceActions.getRatingModuleDetails(String(moduleId)))

    return addCriteria
  },
)

const updateRatingCriterionDetails = createAsyncThunk(
  'updateRatingCriterionDetails',
  async (props: { criterionId: number; module: number; title: string }, { dispatch }) => {
    const updateCriteria = await appAPI.skillsPassport.updateCriterionDetails(props)

    const moduleId = updateCriteria.data.module

    dispatch(ratingSliceActions.getRatingModuleDetails(String(moduleId)))

    return updateCriteria
  },
)

const deleteRatingCriterionDetails = createAsyncThunk(
  'deleteRatingCriterionDetails',
  async (props: { criterionId: number; moduleId: number }, { dispatch }) => {
    const { criterionId, moduleId } = props
    await appAPI.skillsPassport.deleteCriterionDetails(criterionId)

    dispatch(ratingSliceActions.getRatingModuleDetails(String(moduleId)))
  },
)

const createRatingAspect = createAsyncThunk(
  'createRatingAspect',
  async (props: CriterionAspectForModuleCriterionRequest, { dispatch }) => {
    const createAspect = await appAPI.skillsPassport.createAspect(props)

    const criterionId = createAspect.data.criterion

    dispatch(ratingSliceActions.getRatingCriterionDetails(criterionId))

    return createAspect
  },
)

const updateRatingAspectDetails = createAsyncThunk(
  'updateRatingAspectDetails',
  async (props: CriterionAspectForModuleCriterion, { dispatch }) => {
    const updateAspect = await appAPI.skillsPassport.updateAspectDetails(props)

    const criterionId = updateAspect.data.criterion

    dispatch(ratingSliceActions.getRatingCriterionDetails(criterionId))

    return updateAspect
  },
)

const deleteRatingAspectDetails = createAsyncThunk(
  'deleteRatingAspectDetails',
  async (aspectId: number, { dispatch, getState }) => {
    await appAPI.skillsPassport.deleteAspectDetails(aspectId)

    const state = getState() as RootState

    const criteriaId = state.rating.ratingCriterionDetails.data?.id

    if (criteriaId) dispatch(ratingSliceActions.getRatingCriterionDetails(criteriaId))
  },
)

const evaluationTeamAndParticipants = createAsyncThunk(
  'evaluationTeamAndParticipants',
  async (props: { teamId: number; module?: string }, { getState, dispatch }) => {
    const evaluation = await appAPI.skillsPassport.evaluationTeamAndParticipants(props.teamId)

    const state = getState() as RootState

    const championshipsSelectID = state.rating.championshipsSelectID
    const competenciesSelectID = state.rating.competenciesSelectID

    if (evaluation.data.unratedModules && props.module) {
      const unratedModule = evaluation.data.unratedModules.includes(+props.module)

      if (!unratedModule && championshipsSelectID && competenciesSelectID) {
        dispatch(ratingSliceActions.getRatingModuleDetails(props.module))

        dispatch(
          ratingParticipantsSliceActions.getRatingTeams({
            championshipId: championshipsSelectID,
            competenceId: competenciesSelectID,
          }),
        )
      }
    }

    return evaluation
  },
)

const evaluationAspect = createAsyncThunk(
  'evaluationAspect',
  async (
    props: { aspectId: number; expertRatings: string[]; teamID: number; module: string; skipRefetch?: boolean },
    { dispatch },
  ) => {
    const evaluationAspect = await appAPI.skillsPassport.evaluationAspect(props.aspectId, props.expertRatings)

    if (!props.skipRefetch) {
      dispatch(ratingSliceActions.evaluationTeamAndParticipants({ teamId: props.teamID, module: props.module }))
    }

    return evaluationAspect
  },
)

const openEvaluationModule = createAsyncThunk(
  'openEvaluationModule',
  async (props: { teamId: number; moduleId: number }, { getState, dispatch }) => {
    const evaluation = await appAPI.skillsPassport.openEvaluationModule(props.teamId, props.moduleId)

    const state = getState() as RootState
    const championshipsSelectID = state.rating.championshipsSelectID
    const competenciesSelectID = state.rating.competenciesSelectID

    const unratedModule = evaluation.data.unratedModules?.includes(props.moduleId)

    if (!unratedModule && championshipsSelectID && competenciesSelectID) {
      dispatch(ratingSliceActions.getRatingModuleDetails(String(props.moduleId)))

      dispatch(
        ratingParticipantsSliceActions.getRatingTeams({
          championshipId: championshipsSelectID,
          competenceId: competenciesSelectID,
        }),
      )
    }

    return evaluation
  },
)

const blockingStructureSettings = createAsyncThunk(
  'blockingStructureSettings',
  async (props: { championshipsSelectID: string | number; competenciesSelectID: string | number }, { dispatch }) => {
    const { championshipsSelectID, competenciesSelectID } = props
    await appAPI.skillsPassport.blockingStructureSettings(championshipsSelectID, competenciesSelectID)

    dispatch(ratingSliceActions.getEvalStructure({ championshipsSelectID, competenciesSelectID }))
  },
)

const blockingStructureEvaluate = createAsyncThunk(
  'blockingStructureEvaluate',
  async (
    props: { championshipsSelectID: string | number; competenciesSelectID: string | number; unblockSettings?: boolean },
    { dispatch },
  ) => {
    const { championshipsSelectID, competenciesSelectID, unblockSettings } = props

    if (unblockSettings)
      await appAPI.skillsPassport.blockingStructureSettings(championshipsSelectID, competenciesSelectID)

    await appAPI.skillsPassport.blockingStructureEvaluate(championshipsSelectID, competenciesSelectID)

    dispatch(ratingSliceActions.getEvalStructure({ championshipsSelectID, competenciesSelectID }))
  },
)

const blockingModuleEvaluate = createAsyncThunk(
  'blockingModuleEvaluate',
  async (props: { moduleId: string | number; detail?: boolean }, { dispatch, getState }) => {
    await appAPI.skillsPassport.blockingModuleEvaluate(props.moduleId)

    const state = getState() as RootState

    const championshipsSelectID = state.rating.championshipsSelectID
    const competenciesSelectID = state.rating.competenciesSelectID

    if (props.detail) {
      dispatch(ratingSliceActions.getRatingModuleDetails(String(props.moduleId)))
    } else {
      if (championshipsSelectID && competenciesSelectID)
        dispatch(ratingSliceActions.getEvalStructure({ championshipsSelectID, competenciesSelectID }))
    }
  },
)

const ratingSlice = createSlice({
  name: 'rating',
  initialState: initialState,
  reducers: {
    setRatingEntryStatus(state, { payload }: PayloadAction<string>) {
      if (payload === 'customization' || payload === 'estimation' || payload === 'blocked')
        state.ratingEntryStatus = payload
    },
    setChampionshipsSelectID(state, { payload }: PayloadAction<number>) {
      state.championshipsSelectID = payload
      localStorage.setItem('championshipID', String(payload))
    },
    setCompetenciesSelectID(state, { payload }: PayloadAction<number>) {
      state.competenciesSelectID = payload
      localStorage.setItem('competenceID', String(payload))
    },
    clearRatingModuleDetail(state) {
      state.ratingModuleDetail.data = undefined
    },
    clearRatingCriterionDetail(state) {
      state.ratingCriterionDetails.data = undefined
    },
    clearRatingNewModule(state) {
      state.addNewModule = undefined
    },
    clearUpdateModuleDetailsStatus(state) {
      state.ratingModuleDetail.updateStatus = undefined
    },
    setClearEvaluationTeamAndParticipants(state) {
      state.evaluationDetail.data = undefined
    },
    setEmptyRatingData(state, { payload }: PayloadAction<boolean>) {
      state.emptyRatingData = payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(uploadModulesFile.pending, (state) => {
        state.uploadFileLoading = true
      })
      .addCase(uploadModulesFile.fulfilled, (state) => {
        state.uploadFileLoading = false
      })
      .addCase(uploadModulesFile.rejected, (state) => {
        state.uploadFileLoading = false
        state.uploadFileError = true
      })
      .addCase(getEvalStructure.pending, (state) => {
        state.ratingModules.loading = true
        state.ratingModules.data = undefined
      })
      .addCase(getEvalStructure.fulfilled, (state, { payload }) => {
        state.ratingModules.loading = false
        state.ratingModules.data = payload.data

        if (!payload.data.settingsBlock && !payload.data.evaluateBlock) {
          state.ratingEntryStatus = 'customization'
          localStorage.setItem('ratingEntryStatus', 'customization')
        }

        if (payload.data.settingsBlock) {
          state.ratingEntryStatus = 'estimation'
          localStorage.setItem('ratingEntryStatus', 'estimation')
        }

        if (payload.data.evaluateBlock) {
          state.ratingEntryStatus = 'blocked'
          localStorage.setItem('ratingEntryStatus', 'blocked')
        }
      })
      .addCase(getEvalStructure.rejected, (state) => {
        state.ratingModules.loading = false
        state.ratingModules.data = undefined
      })
      .addCase(getRatingModuleDetails.pending, (state) => {
        state.ratingModuleDetail.loading = true
      })
      .addCase(getRatingModuleDetails.fulfilled, (state, { payload }) => {
        state.ratingModuleDetail.loading = false

        if (payload.data.moduleCriterions && payload.data.moduleCriterions?.length > 0) {
          const sortedData = payload.data.moduleCriterions.sort((a, b) => a.id - b.id)
          state.ratingModuleDetail.data = { ...payload.data, moduleCriterions: sortedData }
        } else {
          state.ratingModuleDetail.data = payload.data
        }
      })
      .addCase(getRatingModuleDetails.rejected, (state) => {
        state.ratingModuleDetail.loading = false
      })
      .addCase(createRatingModule.fulfilled, (state, { payload }) => {
        state.addNewModule = payload.data
      })
      .addCase(updateRatingModuleDetails.fulfilled, (state) => {
        state.ratingModuleDetail.updateStatus = 'success'
      })
      .addCase(updateRatingModuleDetails.rejected, (state) => {
        state.ratingModuleDetail.updateStatus = 'error'
      })
      .addCase(getRatingCriterionDetails.pending, (state) => {
        state.ratingCriterionDetails.loading = true

        if (state.ratingModuleDetail.criteriaDeleteStatus !== undefined)
          state.ratingModuleDetail.criteriaDeleteStatus = undefined
      })
      .addCase(getRatingCriterionDetails.fulfilled, (state, { payload }) => {
        state.ratingCriterionDetails.loading = false
        state.ratingCriterionDetails.data = payload.data
      })
      .addCase(getRatingCriterionDetails.rejected, (state) => {
        state.ratingCriterionDetails.loading = false
      })
      .addCase(updateRatingCriterionDetails.pending, (state) => {
        state.ratingCriterionDetails.loading = true
      })
      .addCase(updateRatingCriterionDetails.fulfilled, (state, { payload }) => {
        state.ratingCriterionDetails.loading = false
        state.ratingCriterionDetails.data = payload.data
      })
      .addCase(updateRatingCriterionDetails.rejected, (state) => {
        state.ratingCriterionDetails.loading = false
      })
      .addCase(deleteRatingCriterionDetails.pending, (state) => {
        if (state.ratingModuleDetail.criteriaDeleteStatus !== undefined)
          state.ratingModuleDetail.criteriaDeleteStatus = undefined
      })
      .addCase(deleteRatingCriterionDetails.fulfilled, (state) => {
        state.ratingModuleDetail.criteriaDeleteStatus = 'success'
      })
      .addCase(deleteRatingCriterionDetails.rejected, (state) => {
        state.ratingModuleDetail.criteriaDeleteStatus = 'error'
      })

      .addCase(evaluationTeamAndParticipants.pending, (state, action) => {
        state.evaluationDetail.loading = true
        state.evaluationDetail.latestRequestId = action.meta.requestId
      })
      .addCase(evaluationTeamAndParticipants.fulfilled, (state, action) => {
        if (state.evaluationDetail.latestRequestId !== action.meta.requestId) return

        state.evaluationDetail.data = action.payload.data
        state.evaluationDetail.loading = false
      })
      .addCase(evaluationTeamAndParticipants.rejected, (state, action) => {
        if (state.evaluationDetail.latestRequestId !== action.meta.requestId) return

        state.evaluationDetail.data = undefined
        state.evaluationDetail.loading = false
      })
      .addCase(openEvaluationModule.pending, (state, action) => {
        state.evaluationDetail.loading = true
        state.evaluationDetail.latestRequestId = action.meta.requestId
      })
      .addCase(openEvaluationModule.fulfilled, (state, action) => {
        if (state.evaluationDetail.latestRequestId !== action.meta.requestId) return

        state.evaluationDetail.data = action.payload.data
        state.evaluationDetail.loading = false
      })
      .addCase(openEvaluationModule.rejected, (state, action) => {
        if (state.evaluationDetail.latestRequestId !== action.meta.requestId) return

        state.evaluationDetail.data = undefined
        state.evaluationDetail.loading = false
      })
  },
})

export default ratingSlice.reducer

export const ratingSliceActions = {
  ...ratingSlice.actions,
  uploadModulesFile,
  getEvalStructure,
  createEvalStructure,
  updateEvalStructure,
  getRatingModuleDetails,
  createRatingModule,
  updateRatingModuleDetails,
  deleteRatingModuleDetails,
  getRatingCriterionDetails,
  createRatingCriteria,
  updateRatingCriterionDetails,
  deleteRatingCriterionDetails,
  createRatingAspect,
  updateRatingAspectDetails,
  deleteRatingAspectDetails,
  evaluationTeamAndParticipants,
  openEvaluationModule,
  evaluationAspect,
  blockingStructureSettings,
  blockingStructureEvaluate,
  blockingModuleEvaluate,
}
